import { useState, useEffect, useRef } from 'react'
import { AxiosResponse } from 'axios'

import { filterByOnlySelectedVerses, getVerseDetails, IBibleTextObj } from '../../utils/scripture-modal/bible-utils'
import { fetchBibleChapter } from '../../services/bible'
import { INewBibleResponse, INewVerse } from '../../utils/types'

const useScriptureModal = (scriptureText: string) => {
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState('')
	const [allVerses, setAllVerses] = useState<INewVerse[]>([])
	const [verses, setVerses] = useState<INewVerse[]>([])
	const [isSelectedVerses, setIsSelectedVerses] = useState(true)
	const [bibleTextObj, setBibleTextObj] = useState<IBibleTextObj>({} as IBibleTextObj)
	// Chapter currently held in allVerses. Only set once a fetch has actually
	// succeeded, so a failed fetch never gets treated as "already loaded".
	const loadedChapterKey = useRef<string>('')
	// Incremented per request so a slow response for an earlier tap can't
	// overwrite the verses of a later tap.
	const requestId = useRef(0)

	useEffect(() => {
		if (scriptureText) {
			fetchBibleVerses(scriptureText)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [scriptureText])

	const fetchBibleVerses = async (text: string) => {
		const thisRequest = ++requestId.current
		setIsSelectedVerses(true)
		setError('')

		let textObj: IBibleTextObj
		try {
			textObj = getVerseDetails(text)
		} catch {
			setVerses([])
			setError(`Could not find "${text}"`)
			setLoading(false)
			return
		}
		setBibleTextObj(textObj)

		const chapterKey = `${textObj.bookId}:${textObj.chapter}`

		if (chapterKey === loadedChapterKey.current) {
			setVerses(filterByOnlySelectedVerses(allVerses, textObj.verses))
			setLoading(false)
			return
		}

		// Never leave the previous passage on screen under the new heading
		setVerses([])
		setLoading(true)

		try {
			const res = (await fetchBibleChapter(textObj.bookId, textObj.chapter)) as unknown as AxiosResponse<INewBibleResponse>
			if (thisRequest !== requestId.current) return

			const content = res.data?.content ?? []
			if (content.length === 0) throw new Error('Empty chapter')

			loadedChapterKey.current = chapterKey
			setAllVerses(content)
			setVerses(filterByOnlySelectedVerses(content, textObj.verses))
		} catch {
			if (thisRequest !== requestId.current) return
			loadedChapterKey.current = ''
			setAllVerses([])
			setVerses([])
			setError('Could not load this passage. Check your connection and tap the scripture again.')
		} finally {
			if (thisRequest === requestId.current) setLoading(false)
		}
	}

	const getSelectedBibleVerses = () => {
		setVerses(filterByOnlySelectedVerses(allVerses, bibleTextObj.verses))
		setIsSelectedVerses(true)
	}

	const getFullChapter = () => {
		setVerses(allVerses)
		setIsSelectedVerses(false)
	}

	const retry = () => {
		if (scriptureText) fetchBibleVerses(scriptureText)
	}

	return { loading, error, retry, verses, getSelectedBibleVerses, getFullChapter, isSelectedVerses, bibleTextObj }
}

export default useScriptureModal
